/**
 * V1 Matchmaking — Strength-Band Overlap Selection
 *
 * Per V1 Product Spec:
 * - Strength-band overlap required
 * - Constrained randomness within band
 * - No ladder browsing
 * - No opponent shopping
 * - Cooldown is signal-based (new qualifying lift or accepting a challenge)
 *
 * Invariant: Fair opponents, no farming.
 */

import { supabase } from '@/lib/supabase';

export interface MatchCandidate {
  id: string;
  handle: string | null;
  display_name: string | null;
  avatar_url: string | null;
  lifting_score: number;
  gym_id: string | null;
  /** Reason this opponent was selected */
  matchReason: string;
}

export interface MatchResult {
  candidates: MatchCandidate[];
  userScore: number;
  bandMin: number;
  bandMax: number;
  error: string | null;
}

/**
 * Strength band width as a fraction of the user's score.
 * A 30% band means ±15% of user's score.
 */
const BAND_WIDTH_FRACTION = 0.60;

/** Minimum absolute band width (for very low scores) */
const MIN_BAND_WIDTH = 100;

/** Maximum candidates to return */
const MAX_CANDIDATES = 10;

/** Minimum recent duels before repeat opponent cooldown kicks in */
const REPEAT_COOLDOWN_DUELS = 3;

/**
 * Find matched opponents within the user's strength band.
 * Returns a randomized selection — no browsing, no shopping.
 */
export async function findMatchedOpponents(
  userId: string,
  userScore: number,
  userGymId: string | null,
  userGender?: string | null,
): Promise<MatchResult> {
  if (userScore <= 0) {
    return {
      candidates: [],
      userScore,
      bandMin: 0,
      bandMax: 0,
      error: 'You need a strength score to find opponents. Complete your baseline calibration.',
    };
  }

  const halfBand = Math.max(userScore * (BAND_WIDTH_FRACTION / 2), MIN_BAND_WIDTH / 2);
  const bandMin = Math.floor(Math.max(0, userScore - halfBand));
  const bandMax = Math.ceil(userScore + halfBand);

  try {
    // Fetch potential opponents in the strength band (same gender only)
    let query = supabase
      .from('profiles')
      .select('id, handle, display_name, avatar_url, duel_standard_strength_kg, gym_id')
      .neq('id', userId)
      .gte('duel_standard_strength_kg', bandMin)
      .lte('duel_standard_strength_kg', bandMax)
      .not('handle', 'is', null)
      .order('duel_standard_strength_kg', { ascending: false })
      .limit(20);

    // Gender-lock: males vs males, females vs females
    if (userGender && (userGender === 'male' || userGender === 'female')) {
      query = query.eq('gender', userGender);
    }

    const { data: potentials, error } = await query;

    if (error) {
      return { candidates: [], userScore, bandMin, bandMax, error: error.message };
    }

    let pool = potentials || [];

    // If no opponents in band, widen to ALL users with any lifting_score (still same gender)
    if (pool.length === 0) {
      let fallbackQuery = supabase
        .from('profiles')
        .select('id, handle, display_name, avatar_url, duel_standard_strength_kg, gym_id')
        .neq('id', userId)
        .gt('duel_standard_strength_kg', 0)
        .not('handle', 'is', null)
        .order('duel_standard_strength_kg', { ascending: false })
        .limit(20);

      if (userGender && (userGender === 'male' || userGender === 'female')) {
        fallbackQuery = fallbackQuery.eq('gender', userGender);
      }

      const { data: allUsers } = await fallbackQuery;
      pool = allUsers || [];
    }

    if (pool.length === 0) {
      return {
        candidates: [],
        userScore,
        bandMin,
        bandMax,
        error: 'No opponents available yet. Invite others to join and compete.',
      };
    }

    // Fetch recent duel opponents for cooldown
    const { data: recentDuels } = await supabase
      .from('duels')
      .select('challenger_id, challenged_id')
      .or(`challenger_id.eq.${userId},challenged_id.eq.${userId}`)
      .order('created_at', { ascending: false })
      .limit(REPEAT_COOLDOWN_DUELS);

    const recentOpponentIds = new Set<string>();
    if (recentDuels) {
      for (const d of recentDuels) {
        if (d.challenger_id === userId) recentOpponentIds.add(d.challenged_id);
        else recentOpponentIds.add(d.challenger_id);
      }
    }

    // Filter out recent opponents, prefer same gym
    const filtered = pool.filter((p) => !recentOpponentIds.has(p.id));
    const finalPool = filtered.length > 0 ? filtered : pool; // fallback if all are recent

    // Prioritize same-gym, then shuffle
    const sameGym = userGymId ? finalPool.filter((p) => p.gym_id === userGymId) : [];
    const otherGym = finalPool.filter((p) => !userGymId || p.gym_id !== userGymId);

    // Shuffle each group
    const shuffled = [...shuffle(sameGym), ...shuffle(otherGym)];

    const candidates: MatchCandidate[] = shuffled.slice(0, MAX_CANDIDATES).map((p) => ({
      id: p.id,
      handle: p.handle,
      display_name: p.display_name,
      avatar_url: p.avatar_url,
      lifting_score: p.duel_standard_strength_kg ?? 0,
      gym_id: p.gym_id,
      matchReason: buildMatchReason(userScore, p.duel_standard_strength_kg ?? 0, p.gym_id === userGymId && !!userGymId),
    }));

    return { candidates, userScore, bandMin, bandMax, error: null };
  } catch (err: unknown) {
    return { candidates: [], userScore, bandMin, bandMax, error: err instanceof Error ? err.message : 'Matchmaking failed' };
  }
}

function buildMatchReason(userScore: number, opponentScore: number, sameGym: boolean): string {
  const diff = Math.abs(opponentScore - userScore);
  const pct = userScore > 0 ? Math.round((diff / userScore) * 100) : 0;

  let reason = `Within ${pct}% of your strength score`;
  if (sameGym) {
    reason += ' · Same gym';
  } else {
    reason += ' · Cross-gym';
  }
  return reason;
}

function shuffle<T>(arr: T[]): T[] {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Check if a user has a signal-based cooldown active.
 * A user can initiate a new duel only if they have:
 * 1. Logged a new qualifying lift since their last duel, OR
 * 2. Are accepting an incoming challenge
 */
export async function checkDuelCooldown(userId: string): Promise<{
  canInitiate: boolean;
  reason: string | null;
}> {
  try {
    // Get last duel completion
    const { data: lastDuel } = await supabase
      .from('duels')
      .select('created_at')
      .eq('challenger_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (!lastDuel) {
      // No previous duels — can always initiate
      return { canInitiate: true, reason: null };
    }

    // Check if there's a lift logged after the last duel
    const { data: recentLift } = await supabase
      .from('lifts')
      .select('lifted_at')
      .eq('user_id', userId)
      .gt('lifted_at', lastDuel.created_at)
      .limit(1)
      .single();

    if (recentLift) {
      return { canInitiate: true, reason: null };
    }

    return {
      canInitiate: false,
      reason: 'Log a new qualifying lift to initiate your next duel. You can still accept incoming challenges.',
    };
  } catch {
    // On error, allow (fail-open for now)
    return { canInitiate: true, reason: null };
  }
}
